import { useParams, useNavigate } from 'react-router';
import { Container } from '@components/ui/Container';
import { Section } from '@components/ui/Section';
import { ButtonAnchor } from '@components/ui/Button/ButtonAnchor';
import { Button } from '@components/ui/Button';
import { FadeIn } from '@components/motion/FadeIn';
import { ScrollReveal } from '@components/motion/ScrollReveal';
import { Eyebrow, H2, H3, GradientText } from '@components/ui/Typography';
import { getEventBySlug } from '@data/events';
import { ROUTES } from '@constants/routes';
import { whatsappUrl } from '@constants/company';
import {
  HeroSection,
  HeroBg,
  HeroOverlay,
  HeroGrid,
  HeroContent,
  HeroBadgeRow,
  HeroBadge,
  HeroStatusBadge,
  HeroTitle,
  HeroTagline,
  InfoBar,
  InfoBarInner,
  InfoItem,
  InfoLabel,
  InfoValue,
  ContentGrid,
  ContentMain,
  ContentSidebar,
  DescriptionParagraph,
  SideCard,
  SideCardHeader,
  SideCardTitle,
  SideCardSubtitle,
  SideCardBody,
  SideCardButtons,
  TagsRow,
  Tag,
  BackLink,
  NotFoundWrapper,
} from './styles';
import { memo, useEffect } from 'react';
// import { PageTransition } from '@/components';

/* ── Helpers ──────────────────────────────────────────────── */

const categoryLabel: Record<string, string> = {
  festival: 'Festival',
  varejo: 'Varejo',
  gastronomico: 'Gastronomia',
  corporativo: 'Corporativo',
  rural: 'Rural',
  exposicao: 'Exposição',
};

const statusLabel: Record<string, string> = {
  upcoming: 'Em breve',
  ongoing: 'Em andamento',
  past: 'Edição encerrada',
};

const gradientMap: Record<string, string> = {
  navy: 'linear-gradient(135deg, #0C1F2E 0%, #183C54 100%)',
  steel: 'linear-gradient(135deg, #183C54 0%, #186C9C 100%)',
  cyan: 'linear-gradient(135deg, #186C9C 0%, #00A8F0 100%)',
};

/* ═══════════════════════════════════════════════════════════
   PÁGINA EVENTO INDIVIDUAL
══════════════════════════════════════════════════════════════ */

const EventDetail: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const event = slug ? getEventBySlug(slug) : undefined;

  /* Evento não encontrado */
  if (!event) {
    return (
      <>
        <title>Evento não encontrado — Teia de Eventos</title>
        <NotFoundWrapper>
          <span style={{ fontSize: '3rem' }}>🗓️</span>
          <H2>Evento não encontrado.</H2>
          <Button variant="primary" onClick={() => navigate(ROUTES.events)}>
            Ver todos os eventos
          </Button>
        </NotFoundWrapper>
      </>
    );
  }

  const gradient = gradientMap[event.accentColor ?? 'steel'];
  const whatsappMsg = `Olá! Tenho interesse em ser expositor no evento ${event.name}.`;

  useEffect(() => {
    document.title = `${event.name} — Teia de Eventos`;
  }, [event.name]);

  return (
    <>
      <meta name="description" content={event.shortDescription} />

      {/* HERO */}
      <HeroSection>
        <HeroBg
          $image={event.cover}
          $color={gradient}
          initial={{ scale: 1.05 }}
          animate={{ scale: 1 }}
          transition={{ duration: 1.2, ease: [0.16, 1, 0.3, 1] }}
        />
        <HeroOverlay />
        <HeroGrid />

        <Container>
          <HeroContent>
            {/* Back */}
            <FadeIn direction="up" delay={0.1}>
              <BackLink onClick={() => navigate(ROUTES.events)}>← Todos os eventos</BackLink>
            </FadeIn>

            {/* Badges */}
            <FadeIn direction="up" delay={0.2}>
              <HeroBadgeRow>
                <HeroBadge>{categoryLabel[event.category]}</HeroBadge>
                <HeroStatusBadge $status={event.status}>
                  {statusLabel[event.status]}
                </HeroStatusBadge>
                {event.edition && <HeroBadge>{event.edition}</HeroBadge>}
              </HeroBadgeRow>
            </FadeIn>

            {/* Título */}
            <HeroTitle
              initial={{ opacity: 0, y: 40 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1], delay: 0.3 }}
            >
              {event.name}
            </HeroTitle>

            {/* Tagline */}
            <HeroTagline
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1], delay: 0.45 }}
            >
              {event.tagline}
            </HeroTagline>
          </HeroContent>
        </Container>
      </HeroSection>

      {/* INFO BAR */}
      <InfoBar>
        <Container>
          <InfoBarInner>
            <InfoItem>
              <InfoLabel>Período</InfoLabel>
              <InfoValue>{event.dateLabel}</InfoValue>
            </InfoItem>
            <InfoItem>
              <InfoLabel>Local</InfoLabel>
              <InfoValue>{event.location.name}</InfoValue>
            </InfoItem>
            <InfoItem>
              <InfoLabel>Cidade</InfoLabel>
              <InfoValue>
                {event.location.city}, {event.location.state}
              </InfoValue>
            </InfoItem>
            {event.expectedAudience && (
              <InfoItem>
                <InfoLabel>Público esperado</InfoLabel>
                <InfoValue>{event.expectedAudience}</InfoValue>
              </InfoItem>
            )}
          </InfoBarInner>
        </Container>
      </InfoBar>

      {/* CONTEÚDO */}
      <Section $bg="primary" $spacing="xl">
        <Container>
          <ContentGrid>
            {/* Descrição */}
            <ContentMain>
              <ScrollReveal direction="up">
                <Eyebrow>Sobre o evento</Eyebrow>
              </ScrollReveal>

              {event.fullDescription.map((paragraph, i) => (
                <DescriptionParagraph
                  key={i}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, amount: 0.3 }}
                  transition={{
                    duration: 0.6,
                    ease: [0.16, 1, 0.3, 1],
                    delay: i * 0.08,
                  }}
                >
                  {paragraph}
                </DescriptionParagraph>
              ))}

              {/* Tags */}
              {event.tags && event.tags.length > 0 && (
                <ScrollReveal direction="up" delay={0.1}>
                  <TagsRow>
                    {event.tags.map((tag) => (
                      <Tag key={tag}>#{tag}</Tag>
                    ))}
                  </TagsRow>
                </ScrollReveal>
              )}
            </ContentMain>

            {/* Sidebar */}
            <ContentSidebar>
              <ScrollReveal direction="right" distance={40}>
                <SideCard>
                  <SideCardHeader $gradient={gradient}>
                    <SideCardTitle>Participe deste evento</SideCardTitle>
                    <SideCardSubtitle>
                      Exponha sua marca para milhares de visitantes qualificados.
                    </SideCardSubtitle>
                  </SideCardHeader>
                  <SideCardBody>
                    <SideCardButtons>
                      <ButtonAnchor
                        href={whatsappUrl(whatsappMsg)}
                        target="_blank"
                        rel="noopener noreferrer"
                        $variant="primary"
                        $size="md"
                        style={{ width: '100%', justifyContent: 'center' }}
                        whileTap={{ scale: 0.96 }}
                      >
                        Quero ser expositor
                      </ButtonAnchor>
                      <ButtonAnchor
                        href={whatsappUrl()}
                        target="_blank"
                        rel="noopener noreferrer"
                        $variant="secondary"
                        $size="md"
                        style={{ width: '100%', justifyContent: 'center' }}
                        whileTap={{ scale: 0.96 }}
                      >
                        Falar com a equipe
                      </ButtonAnchor>
                    </SideCardButtons>
                  </SideCardBody>
                </SideCard>
              </ScrollReveal>

              {/* Outros eventos */}
              <ScrollReveal direction="right" distance={40} delay={0.1}>
                <SideCard>
                  <SideCardBody>
                    <H3 style={{ fontSize: '1rem' }}>
                      <GradientText>Outros eventos</GradientText>
                    </H3>
                    <Button
                      variant="ghost"
                      size="sm"
                      style={{ width: '100%', justifyContent: 'flex-start', paddingLeft: 0 }}
                      onClick={() => navigate(ROUTES.events)}
                    >
                      Ver calendário completo →
                    </Button>
                  </SideCardBody>
                </SideCard>
              </ScrollReveal>
            </ContentSidebar>
          </ContentGrid>
        </Container>
      </Section>
    </>
  );
};

export default memo(EventDetail);
